import { BaseSetting, TemsWebSetting } from "@/Settings/SettingsManager";
import SiteTreeView from "./SiteTreeView";
import SiteManagerView from "./SiteManagerView";
import PositionControl from "./PositionControl";
import DeviceLinksControl from "./DeviceLinksControl";
import TriggerControl from "./TriggerControl"
import { SiteViewModel, LocationViewModel, ElementViewModel, MessageViewModel, LaneViewModel, DeviceViewModel, DeviceDataPointViewModel } from "@/TemsManagerWebInterface";
import { ModelMessageMode as MessageMode, DataPointState } from "@/TemsManagerWebEnums"
import { ViewModelUserLevel } from 'Tems/Security/TemsPolicy';
import Localization from "@/Localization";
import TemsWebEvents from "@/TemsWebEvents";

class SiteTreeSettings extends BaseSetting {
    public collapsedTreeItems: string[] = [];
}

/*
 Purpose of this class is to encapsulate all functions that are related to build and update the SiteTree
 it is intended to split html and logic related code (in SiteTreeView)
*/
export default class SiteTreeBuilder {

    private static Settings = new TemsWebSetting<SiteTreeSettings>("SiteTreeSetting", new SiteTreeSettings());

    private siteTreeView: SiteTreeView;
    private siteManagerView: SiteManagerView;
    public linksControls: { [id: string]: DeviceLinksControl } = {};
    public triggerControls: { [id: string]: TriggerControl } = {};
    public positionControls: { [id: string]: PositionControl } = {};

    constructor(siteTreeView: SiteTreeView, siteManagerView: SiteManagerView) {
        this.siteTreeView = siteTreeView;
        this.siteManagerView = siteManagerView;
    }

    /*
     * Generate the entries for the site tree
     */
    public updateSiteTree(newSite: SiteViewModel, oldSite: SiteViewModel): boolean {
        // if the site has changed, we discard all collapsed-settings, to prevent the cookie from overflowing!
        if (newSite.Id && oldSite.Id && newSite.Id !== oldSite.Id) {
            SiteTreeBuilder.Settings.resetDefaults();
        }

        this.initSiteTree($("#tableSiteTree"));

        // ROADS!
        var siteTreeModelChanged = this.updateSiteTreeRoads(newSite, oldSite);

        // PLUGINS!
        siteTreeModelChanged = this.updateSiteTreePlugins(newSite, oldSite) || siteTreeModelChanged;

        return siteTreeModelChanged;
    }

    private initSiteTree(tree: JQuery) {
        var roads = tree.children("ul");
        // very first init, skeleton for roads as children, special child plugins and the add plugin and add roads link
        if (roads.length === 0) {

            roads = $("<ul>");
            this.appendAddElement(roads, "addRoad", Localization.resourceStrings.SiteView_ButtonAddRoad, () => { this.siteTreeView.addRoad(); });

            var plugins = $(`<div id='sitetree-pluginRootElement'>
                                    <img class='element-image' src='/api/Info/GetIcon/$pluginsRoot' />
                                    <span class='element-title'> ${Localization.resourceStrings.SiteView_SiteTreePluginRootTitle} </span>
                                 </div>`);

            var pluginsChildren = $("<ul>");
            plugins.append(pluginsChildren);

            // Make plugins sortable
            pluginsChildren.addClass("sortableElement");

            this.appendAddElement(pluginsChildren, "addPlugin", Localization.resourceStrings.SiteView_ButtonAddPlugin, () => { this.siteTreeView.addPlugin(); });

            tree.append(roads);
            tree.append(plugins);
        }
    }

    private updateSiteTreeRoads(newSite: SiteViewModel, oldSite: SiteViewModel): boolean {
        // NOTE it should be possible to refactor this  method and make use of roadComparison.added
        // then -> remove old elements
        //      -> create new elements
        //      -> update all

        var siteTreeModelChanged = false;
        var roads = $("#tableSiteTree").children("ul");
        var roadComparison = Localization.compareCollections(oldSite.Locations, newSite.Locations, x => x.Id);

        // remove dropped elements
        roadComparison.removed.map(id => {
            roads.find(`[data-id='${id}']`).remove();
            return null;
        });

        roadComparison.same.map((id) => {
            let roads = newSite.Locations.filter((r) => { return id === r.Id; });
            if (roads.length === 1) {
                siteTreeModelChanged = this.updateTreeElement(roads[0]) || siteTreeModelChanged;
                this.updateRoadElement(roads[0]);
            } else {
                // something went wrong! The Id should exist exactly one time.
            }
        });

        siteTreeModelChanged = roadComparison.removed.length > 0 || siteTreeModelChanged;

        if (newSite.Locations) {
            var previousRoadId = null;
            let locationCount = newSite.Locations.length;
            for (var locationIdx = 0; locationIdx < locationCount; locationIdx++) {
                var locationModel = newSite.Locations[locationIdx];
                let locationItem = roads.children(`[data-id='${locationModel.Id}']`);
                if (locationItem.length === 0) {
                    // This is a new road, create the item
                    locationItem = $("<li>", {
                        id: `sitetree-${locationModel.Id}`,
                        'class': "sitetree-entry",
                        'data-id': locationModel.Id
                    });

                    locationItem.append(this.createDomForTreeElement(locationModel, true));
                    this.makeCollapsable(locationItem, locationModel);

                    this.attachRoadSignButton(locationItem, locationModel);
                    if (locationModel.Lanes.length > 0) {
                        this.attachRoadLanesButton(locationItem, locationModel);
                    }
                    this.attachDeleteButton(locationItem, locationModel);
                    this.attachDetailsButton(locationItem, locationModel.Id);

                    if (previousRoadId === null) {
                        roads.prepend(locationItem);
                    } else {
                        roads.children(`[data-id='${previousRoadId}']`).after(locationItem);
                    }

                    this.updateRoadElement(locationModel);

                    siteTreeModelChanged = true;
                }

                siteTreeModelChanged = this.updateDeviceTree(locationItem, locationModel, false, newSite.DeviceLimit, newSite.CanDiscoverSensors) || siteTreeModelChanged;

                previousRoadId = locationModel.Id;
            }

            $.each(this.linksControls, (idx, elem) => {
                elem.determineAddLinkControlState($(elem));
            });
            
            this.updateAddElementClickHandler(roads, "addRoad", () => { this.siteTreeView.addRoad(newSite.Locations); });

            if (this.siteManagerView.IsInEditMode && newSite.Locations.length < newSite.LocationLimit) {
                this.showAddElement(roads, "addRoad");
            } else {
                this.hideAddElement(roads, "addRoad");
            }
        }

        return siteTreeModelChanged;
    }

    private updateRoadElement(road: LocationViewModel) {
        const roadId = road.Id;
        const laneModels = road.Lanes;
        let table = $(`#sitetree-element-roadconfig-${roadId}`).find("table");
        let tbody = table.children("tbody");

        let oldIds: string[] = [];
        tbody.children("tr").each((index, elem) => { oldIds.push(elem.id.substr((`sitetree-lane-details-${roadId}-`).length)) });

        for (let i = 0; i < laneModels.length; i++) {
            var index = oldIds.indexOf(laneModels[i].Id);
            if (index !== -1) {
                oldIds.splice(index, 1);
                this.updateLane(tbody.children(`#sitetree-lane-details-${roadId}-${laneModels[i].Id}`), laneModels[i], laneModels.length);
                continue;
            }

            this.createLane(tbody, laneModels[i], roadId, laneModels.length);
        }

        table.toggleClass("hideHardShoulderColumn", laneModels.length <= 1 && !laneModels.every((lane) => { return lane.IsHardShoulder; }));

        for (let i = 0; i < oldIds.length; i++) {
            tbody.children(`#sitetree-lane-details-${roadId}-${oldIds[i]}`).remove();
        }

        const isSigned = !!road.Signature;
        $(`#sitetree-element-signaturebutton-${roadId}`).find("span").toggleClass("text-success", isSigned);
        $(`#sitetree-element-roadsign-${roadId}`).find("span.siteTreePanelIcon").toggleClass("text-success", isSigned);
        $(`#sitetree-element-roadsign-removebutton-${roadId}`).toggle(isSigned);
        $(`#sitetree-element-roadsign-addbutton-${roadId}`).toggle(!isSigned);
        let signRoadForm = $(`#signRoadForm-${roadId}`);
        signRoadForm.find(".signatureName").prop("disabled", isSigned);
        signRoadForm.find(".signatureNotes").prop("disabled", isSigned);
        signRoadForm.toggle(isSigned || this.siteManagerView.IsInEditMode);
        $(`#sitetree-element-notsignednotice-${roadId}`).toggle(!(isSigned || this.siteManagerView.IsInEditMode));
        $(`#signRoadForm-${roadId}-signatureDate`).toggle(isSigned);

        if (isSigned) {
            signRoadForm.find(".signatureName").val(road.Signature.SignerName);
            signRoadForm.find(".signatureNotes").val(road.Signature.Notes);
            signRoadForm.find(".signatureDate").val(Localization.isoDateTimeToLocal(road.Signature.SigningDate));
        }
    }

    private updateSiteTreePlugins(newSite: SiteViewModel, oldSite: SiteViewModel): boolean {
        var siteTreeModelChanged = false;
        if (!newSite.Locations) {
            $("#sitetree-pluginRootElement").hide();
        } else {
            $("#sitetree-pluginRootElement").show("slow");
        }

        var pluginRoot = $("#sitetree-pluginRootElement").children("ul");

        var pluginComparison = Localization.compareCollections(oldSite.Plugins, newSite.Plugins, x => x.Id);

        // remove dropped elements
        pluginComparison.removed.map(id => {
            pluginRoot.find(`[data-id='${id}']`).remove();
        });
        pluginComparison.same.map((id) => {
            var plugin = newSite.Plugins.filter(p => (id === p.Id));
            siteTreeModelChanged =
                this.updateTreeElement(plugin[0])
                || siteTreeModelChanged;
        });

        if (pluginComparison.added.length > 0) {
            var previousPluginId = null;
            for (var pluginModelIdx = 0, sitePluginsLength = newSite.Plugins.length; pluginModelIdx < sitePluginsLength; pluginModelIdx++) {
                var pluginModel = newSite.Plugins[pluginModelIdx];
                if ($.inArray(pluginModel.Id, pluginComparison.added) > -1) {
                    var pluginItem = $("<li>", {
                        id: `sitetree-${pluginModel.Id}`,
                        'data-id': pluginModel.Id
                    });

                    pluginItem.append(this.createDomForTreeElement(pluginModel, false));

                    this.attachDeleteButton(pluginItem, pluginModel, "onlyExpertMode");
                    this.attachDetailsButton(pluginItem, pluginModel.Id);

                    if (previousPluginId === null) {
                        pluginRoot.prepend(pluginItem);
                    } else {
                        pluginRoot.children(`[data-id='${previousPluginId}']`).after(pluginItem);
                    }

                    siteTreeModelChanged = true;
                }

                previousPluginId = pluginModel.Id;
            }
        }

        if (newSite.Plugins) {
            if (this.siteManagerView.IsInEditMode && newSite.Plugins.length < newSite.PluginLimit) {
                this.showAddElement(pluginRoot, "addPlugin");
            } else {
                this.hideAddElement(pluginRoot, "addPlugin");
            }
        }

        return siteTreeModelChanged;
    }

    private appendAddElement(elemSiteTree: JQuery, id: string, text: string, clickHandler): void {
        var newRow = $(`<div id='${id}' class='editControl siteTreeElement onlyExpertMode' data-add-target='${id}'>
                                <div class='elementAdd'>
                                    <span class='glyphicon glyphicon-plus elementAddIcon'></span>
                                    <span class='elementAddText'>${text}</span>
                                </div>
                            </div>`);

        newRow.click(clickHandler);

        elemSiteTree.append(newRow);
    }

    private hideAddElement(elemSiteTree: JQuery, id: string): void {
        elemSiteTree.children(`#${id}`).hide();
    }

    private showAddElement(elemSiteTree: JQuery, id: string): void {
        elemSiteTree.children(`#${id}`).show();
    }

    private updateAddElementClickHandler(elemSiteTree: JQuery, id: string, clickHandler): void {
        elemSiteTree.children(`#${id}`).unbind("click");
        elemSiteTree.children(`#${id}`).click(clickHandler);
    }

    private updateTreeElement(element: ElementViewModel): boolean {
        var siteChanged = false;
        var domElement = $(`#element-${element.Id}`);
        if (!domElement) {
            return false;
        }

        if (!domElement.hasClass(`label-${Localization.translateHealth(element.Health)}`)) {
            domElement.removeClass(`label-${Localization.translateHealth("Good")}`);
            domElement.removeClass(`label-${Localization.translateHealth("Moderate")}`);
            domElement.removeClass(`label-${Localization.translateHealth("Bad")}`);
            domElement.removeClass(`label-${Localization.translateHealth("Unknown")}`);
            domElement.addClass(`label-${Localization.translateHealth(element.Health)}`);
        }

        var domTitle = domElement.siblings(".element-title").first();
        if (domTitle.text() !== element.Title) {
            if (domTitle.hasClass("editable")) {
                if (domTitle.text() !== ($.fn.editable as any).defaults.emptytext) { // don't update if the text is the empty text, it would overwrite any unconfirmed user input in an update cycle
                    domTitle.editable("setValue", element.Title);
                    siteChanged = true;
                }

            } else {
                domTitle.text(element.Title);
                siteChanged = true; // the title before and after the road has to change
            }
        }

        this.changeBadge(domElement.siblings(".badge").first(), element);

        // do not show control elements when not in edit mode
        if (!this.siteManagerView.IsInEditMode) {
            $(`#sitetree-element-position-${element.Id}`).hide();
            $(`#sitetree-element-links-${element.Id}`).hide();
        }

        return siteChanged;
    }

    private changeBadge(badge: JQuery, element: ElementViewModel) {
        var messagesHtml = this.createMessageTooltipContent(element.Messages);
        var messageSeverity = this.getMessageSeverity(element.Messages);
        var badgeContent = element.Messages.length > 0 && messageSeverity >= MessageMode.Warning ? element.Messages.length.toString() : "";
        var severityClass = `badge-${Localization.messageSeverities(messageSeverity)}`;

        badge.tooltip({ html: true });

        if (badge.prop("title") !== messagesHtml || badge.text() !== badgeContent) {
            badge.prop("title", messagesHtml)
                .tooltip("fixTitle")
                .removeClass()
                .addClass("badge")
                .addClass(severityClass)
                .text(badgeContent);
        }
    }

    private createLane(domElement: JQuery, laneModel: LaneViewModel, roadId: string, laneCount: number) {
        let widthEditable = $("<span>", {
            id: `sitetree-lane-${laneModel.Id}-width`,
            type: "number",
            "class": "siteEditable"
        });
        widthEditable.editable("setValue", laneModel.Width);
        widthEditable.on("save", (e, params) => {
            $.ajax({
                url: "/api/SiteEdit/SaveLaneWidthChange",
                method: "POST",
                data: {
                    Id: laneModel.Id,
                    Value: params.newValue
                },
            }).done(() => {
                this.siteManagerView.forceSiteModelAndViewUpdate();
            }).fail((a, b, c) => {
                alert(Localization.resourceStrings.SiteView_ErrorConfigureLaneWidth);
            });
        });

        let directionEditable = $("<span>", {
            id: `sitetree-lane-${laneModel.Id}-direction`,
            "data-type": "select",
            "data-source": `[{value: 1, text: '${Localization.resourceStrings.SiteView_LaneDirectionNormal}'}, {value: 0, text: '${Localization.resourceStrings.SiteView_LaneDirectionReverse}'}]`,
            "class": "siteEditable"
        });
        directionEditable.editable("setValue", laneModel.NormalDirection ? 1 : 0);
        directionEditable.on("save", (e, params) => {
            $.ajax({
                url: "/api/SiteEdit/SaveLaneDirectionChange",
                method: "POST",
                data: {
                    Id: laneModel.Id,
                    Value: params.newValue === "1"
                },
            }).done(() => {
                this.siteManagerView.forceSiteModelAndViewUpdate();
            }).fail(() => {
                alert(Localization.resourceStrings.SiteView_ErrorConfigureLaneDirection);
            });
        });

        let isHardShoulderInput = $("<input>", {
            id: `sitetree-lane-${laneModel.Id}-isHardShoulder`,
            class: "bootstrap-checkbox",
            type: "checkbox",
        });
        isHardShoulderInput.prop("checked", laneModel.IsHardShoulder);
        isHardShoulderInput.prop("disabled", !this.siteManagerView.IsInEditMode);
        isHardShoulderInput.click(eventObject => {
            $.ajax({
                url: "/api/SiteEdit/SaveLaneIsHardShoulderChange",
                method: "POST",
                data: {
                    Id: laneModel.Id,
                    Value: $(eventObject.target).prop("checked")
                },
            }).done(() => {
                this.siteManagerView.forceSiteModelAndViewUpdate();
            }).fail(() => {
                alert(Localization.resourceStrings.SiteView_ErrorConfigureLaneDirection);
            });
        });

        domElement.append($("<tr>", { id: `sitetree-lane-details-${roadId}-${laneModel.Id}` })
            .append($("<td>").html(`${laneModel.SequenceNumber + 1}`))
            .append($("<td>").append(widthEditable).append("&nbsp;m"))
            .append($("<td>").append(directionEditable))
            .append($("<td>", { class: "isHardShoulderColumn" }).append(isHardShoulderInput)
                .append($("<label>", { class: "customLabel", for: `sitetree-lane-${laneModel.Id}-isHardShoulder` })))
        );

        if (laneCount == 1 && laneModel.IsHardShoulder) {
            isHardShoulderInput.click();
        }
    }

    private updateLane(domElement: JQuery, laneModel: LaneViewModel, laneCount: number) {
        let widthEditable = domElement.find(`#sitetree-lane-${laneModel.Id}-width`);
        let directionEditable = domElement.find(`#sitetree-lane-${laneModel.Id}-direction`);
        let isHardShoulderInput = domElement.find(`#sitetree-lane-${laneModel.Id}-isHardShoulder`);
        let directionNumeral = laneModel.NormalDirection ? 1 : 0;

        if (laneModel.Width !== <any>(widthEditable.editable("getValue", true))) {
            widthEditable.editable("setValue", laneModel.Width)
        }

        if (directionNumeral !== <any>(directionEditable.editable("getValue", true))) {
            directionEditable.editable("setValue", laneModel.NormalDirection ? 1 : 0)
        }

        isHardShoulderInput.prop("checked", laneModel.IsHardShoulder);
        isHardShoulderInput.prop("disabled", !this.siteManagerView.IsInEditMode);
        if (laneCount == 1 && laneModel.IsHardShoulder) {
            isHardShoulderInput.click();
        }
    }

    private createDeviceElement(elemSiteTree: JQuery, deviceModel: DeviceViewModel, parentIsADevice: boolean, canDiscoverSensors: boolean): JQuery {
        var domElement = $("<li>", { "data-id": deviceModel.Id, "class": (parentIsADevice ? "childDeviceElement" : "parentDeviceElement") })
            .toggleClass("hidden", !deviceModel.IsEnabled)
            .append(this.createDomForTreeElement(deviceModel, false));

        if (!parentIsADevice && deviceModel.Devices.length > 0) {
            this.makeCollapsable(domElement, deviceModel);
        }

        if (deviceModel.IsMovable) {
            this.attachPositionButton(domElement, deviceModel);
        }

        this.attachLiveDataButton(domElement, deviceModel);

        if (deviceModel.TriggerSettings) {
            this.attachTriggerButton(domElement, deviceModel);
        }

        if (deviceModel.Links) {
            this.createLinksTab(domElement, deviceModel);
        }

        if (parentIsADevice) {
            if (deviceModel.IsOptional) {
                this.attachEnableDeviceButton(domElement, deviceModel);
            }
        } else {
            if (deviceModel.IsOptional) {
                this.attachEnableAllChildDevicesButton(domElement, deviceModel);
            }

            this.attachDeleteButton(domElement, deviceModel, "onlyExpertMode");
        }

        if (canDiscoverSensors && deviceModel.SupportedSensorTypes.length > 0) {
            this.attachSensorDiscoveryButton(domElement, deviceModel);
        }

        if (this.checkForExpertModeDevice(deviceModel)) {
            domElement.addClass("onlyExpertMode");
        }

        this.attachDetailsButton(domElement, deviceModel.Id);

        return domElement;
    }

    private checkForExpertModeDevice(deviceModel: DeviceViewModel): boolean {
        if (deviceModel.SupportedSensorTypes.length > 0) {
            return false;
        }

        if (deviceModel.IsMovable) {
            return false;
        }

        if (deviceModel.DeviceDataPoints.length > 0) {
            return false;
        }

        if (deviceModel.Devices.length > 0) {
            for (let i = 0; i < deviceModel.Devices.length; i++) {
                if (this.checkForExpertModeDevice(deviceModel.Devices[i])) {
                    return true;
                }
            }

            return false;
        }

        if (deviceModel.Messages.length > 0) {
            return false;
        }

        return true;
    }

    private updateDeviceTree(parentDom: JQuery, parentModel: DeviceViewModel | LocationViewModel, parentIsADevice: boolean, deviceLimit: number, canDiscoverSensors: boolean): boolean {
        var siteTreeModelChanged = false;
        var deviceList = parentDom.children("ul");

        // Create the list if it doesn't exist
        if (deviceList.length === 0) {
            deviceList = $("<ul>");
            parentDom.append(deviceList);
            if (!parentIsADevice) { // i.e. parent is a road
                this.appendAddElement(deviceList, `addDevice-${parentModel.Id}`, Localization.resourceStrings.SiteView_ButtonAddDevice, () => { this.siteTreeView.addDevice(parentModel.Id); });
                deviceList.addClass("sortableElement");
            }

            let initAsCollapsed = SiteTreeBuilder.Settings.Current.collapsedTreeItems.indexOf(parentModel.Id) >= 0;
            if (initAsCollapsed) {
                deviceList.hide();
            }
        }

        // Compare current to previous state
        var newDeviceIds = parentModel.Devices.map(d => d.Id);
        var oldDeviceIds = [];
        deviceList.children("[data-id]").each((index, elem) => { oldDeviceIds.push($(elem).attr("data-id")); });
        var deviceComparison = Localization.compareCollections(oldDeviceIds, newDeviceIds, x => x);

        // Remove devices that are no longer present on the server
        deviceComparison.removed.map(id => {
            deviceList.find(`[data-id='${id}']`).remove();
        });

        // Update the devices that are still present on the server
        deviceComparison.same.map((id) => {
            var device: DeviceViewModel[] = parentModel.Devices.filter((d) => { return id === d.Id; });
            var deviceDomElement = deviceList.find(`[data-id='${id}']`);

            // Check whether the user wants this device to show all child devices
            var showAllChildDevices = parentDom.hasClass("showAllChildDevices");

            if (device[0].IsEnabled) {
                deviceDomElement.removeClass("hidden");
                if (deviceDomElement.hasClass("childDeviceElement")) {
                    deviceDomElement.find("div > button.toggleEnableControl")
                        .toggleClass("hidden", !showAllChildDevices) // Show or hide the enable / disable buttons
                        .find("span")
                        .removeClass("glyphicon-unchecked") // Change the icon to reflect the enabled / disable state
                        .addClass("glyphicon-check");
                }
            } else {
                deviceDomElement.toggleClass("hidden", !showAllChildDevices);
                deviceDomElement.find("div > button.toggleEnableControl")
                    .toggleClass("hidden", !showAllChildDevices) // Show or hide the enable / disable buttons
                    .find("span")
                    .removeClass("glyphicon-check") // Change the icon to reflect the enabled / disable state
                    .addClass("glyphicon-unchecked");
            }

            const triggerControl = this.triggerControls[device[0].Id];
            if (triggerControl) {
                triggerControl.updateTriggerButton(device[0], this.siteManagerView.IsInEditMode);
            }

            const linksControl = this.linksControls[device[0].Id];
            if (linksControl) {
                linksControl.updateLinks(device[0]);
                let linksButton = deviceDomElement.find(`#siteTreeButton-toggleLinksTab-${device[0].Id}`);
                if (device[0].Links.length === 0 || !this.siteManagerView.IsInEditMode) {
                    if (linksButton.hasClass("active")) {
                        linksButton.click();
                    }

                    linksButton.hide();
                } else {
                    linksButton.show();
                }
            }

            const positionControl = this.positionControls[device[0].Id];
            if (positionControl) {
                positionControl.updateDeviceModel(device[0]);
            }

            this.updateDataStreamButtons(device[0]);

            siteTreeModelChanged =
                this.updateTreeElement(device[0])
                || siteTreeModelChanged;
        });

        siteTreeModelChanged = deviceComparison.removed.length > 0 || siteTreeModelChanged;

        if (parentModel.Devices.length > 0) {
            var prevDeviceModelId = null;
            for (var deviceModelIdx = 0, rmDevlen = parentModel.Devices.length; deviceModelIdx < rmDevlen; deviceModelIdx++) {
                var deviceModel = parentModel.Devices[deviceModelIdx];
                var deviceElement = deviceList.children(`[data-id='${deviceModel.Id}']`);
                if (deviceElement.length === 0) {
                    deviceElement = this.createDeviceElement(deviceList, deviceModel, parentIsADevice, canDiscoverSensors);

                    if (prevDeviceModelId === null) {
                        deviceList.prepend(deviceElement);
                    } else {
                        deviceList.children(`[data-id='${prevDeviceModelId}']`).after(deviceElement);
                    }

                    siteTreeModelChanged = true;
                }

                siteTreeModelChanged = this.updateDeviceTree(deviceElement, deviceModel, true, deviceLimit, canDiscoverSensors) || siteTreeModelChanged;

                prevDeviceModelId = deviceModel.Id;
            }
        }

        if (!parentIsADevice && this.siteManagerView.IsInEditMode && parentModel.Devices.length < deviceLimit) {
            this.showAddElement(deviceList, `addDevice-${parentModel.Id}`);
        } else {
            this.hideAddElement(deviceList, `addDevice-${parentModel.Id}`);
        }

        return siteTreeModelChanged;
    }

    private createDomForTreeElement(element: ElementViewModel, affectsSiteView: boolean): JQuery {
        var messageCount = element.Messages.length;
        var messageSeverity = this.getMessageSeverity(element.Messages);
        var severityClass = "";
        var badgeContent = "";
        var messagesHtml = this.createMessageTooltipContent(element.Messages);

        if (messageCount > 0 && messageSeverity >= MessageMode.Warning) {
            severityClass = `badge-${Localization.messageSeverities(messageSeverity)}`;
            badgeContent = messageCount.toString();
        }

        // NOTE: if you change the <span><img>, also adjust makeCollapsable! The issue is that <img> doesn't support :before!
        var $el = $(`<div class='row siteTreeElement' style='margin-left:0px; margin-right:0px' >
                        <div class='showElementDetails flexGrow1'>
                            <span class="glyphicon"><img class='element-image' src='/api/Info/GetIcon/${element.IconId}' /></span>
                            <span id='element-${element.Id}' class='label label-${Localization.translateHealth(element.Health)} element-state'></span>
                            <span class='element-title siteEditable newEditableElement' data-type='text' data-id='${element.Id}' data-affectsSiteView='${affectsSiteView}'>${element.Title}</span>
                            <span class='badge ${severityClass}' data-toggle='tooltip' data-placement='top' data-html='true' title='${messagesHtml}'>${badgeContent}</span>
                        </div>
                        <div class='siteTreeButtons'></div>
                    </div>`);
        this.changeBadge($el.find('.badge').first(), element);
        return $el;
    }

    private makeCollapsable(domElement: JQuery, deviceModel: ElementViewModel) {
        const initAsCollapsed = SiteTreeBuilder.Settings.Current.collapsedTreeItems.indexOf(deviceModel.Id) >= 0;

        domElement.find(".element-image").parent() // span
            .toggleClass("collapsable", true)
            .toggleClass(initAsCollapsed ? "glyphicon-menu-right" : "glyphicon-menu-down", true)
            .click(this.collapseClickHandler(deviceModel));
    }

    private collapseClickHandler(deviceModel: ElementViewModel): (x: JQuery.ClickEvent) => any {
        return (event) => {
            event.stopPropagation();

            const isVisibleBefore = $(`[data-id='${deviceModel.Id}']`).children("ul").is(":visible");
            $(event.currentTarget).toggleClass("glyphicon-menu-right", isVisibleBefore);
            $(event.currentTarget).toggleClass("glyphicon-menu-down", !isVisibleBefore);

            $(`[data-id='${deviceModel.Id}']`).children("ul").slideToggle(400, () => {
                SiteTreeBuilder.Settings.updateSettings(s => {
                    const isVisibleAfter = $(`[data-id='${deviceModel.Id}']`).children("ul").is(":visible");
                    if (isVisibleAfter) {
                        s.collapsedTreeItems.splice(s.collapsedTreeItems.indexOf(deviceModel.Id), 1);
                    } else {
                        s.collapsedTreeItems.push(deviceModel.Id);
                    }
                });
            });
        }
    }

    private createMessageTooltipContent(messages: MessageViewModel[]): string {
        var messagesList = $("<ul>", { class: "list-group" });
        $.each(messages, (index, message) => {
            messagesList.append($("<li>", { class: "list-group-item", html: message.Message }).prepend(
                `<img class='element-image' src= '/img/ElementConfigMessageMode${MessageMode[message.Mode]}.png' height= '16' width= '16' style= 'height:16px; width:16px;' />`
            ));
        });

        return messagesList[0].outerHTML;
    }

    private getMessageSeverity(messages: MessageViewModel[]): MessageMode {
        var messageSeverity = MessageMode.Debug;
        $.each(messages, (index, message) => {
            if (message.Mode > messageSeverity) {
                messageSeverity = message.Mode;
                if (message.Mode === MessageMode.Error) {
                    return false;
                }
            }
        });

        return messageSeverity;

    }

    private createSiteTreeDetailsPanel(idAttribute: string, glyphIconClass: string, panelStyles: string = ""): JQuery {
        return $(`<div id='${idAttribute}' class='panel panel-default hidden siteTreePanel' style='position: relative; ${panelStyles}'>
                          <span class='siteTreePanelIcon glyphicon ${glyphIconClass}'></span>
                      </div>`);
    }

    private createSiteTreeButton(domElement: JQuery, clickHandler: (eventObject: JQuery.ClickEvent) => any, iconClass: string, title: string, editOnly: boolean = true, additionalClasses: string = "", buttonId: string = undefined) {
        domElement.children("div").children(".siteTreeButtons")
            .append(this.createButton(clickHandler, iconClass, title, editOnly, additionalClasses, buttonId));
    }

    private createButton(clickHandler: (eventObject: JQuery.ClickEvent) => any, iconClass: string, title: string, editOnly: boolean = true, additionalClasses: string = "", buttonId: string = undefined): JQuery {
        return $("<button>", buttonId ? { "id": buttonId } : {})
            .addClass(`btn btn-default btn-xs${editOnly ? " editControl " : " "}${additionalClasses}`)
            .attr("title", title)
            .append($("<span>").addClass(`glyphicon ${iconClass}`))
            .click(clickHandler);
    }

    private attachLiveDataButton(domElement: JQuery, deviceModel: DeviceViewModel) {
        let liveDataTab = this.createSiteTreeDetailsPanel(`sitetree-element-livedata-${deviceModel.Id}`, "glyphicon-eye-open");
        liveDataTab.append($("<div>", { 'class': "dataStreamList button-group", role: "group" }));
        this.createSiteTreeButton(
            domElement,
            (event) => {
                $(event.currentTarget).toggleClass("active");
                $(`#sitetree-element-livedata-${deviceModel.Id}`).toggleClass("hidden");
            },
            "glyphicon-eye-open",
            Localization.resourceStrings.SiteTreeBuilder_ButtonTitleLiveDataView,
            false,
            "hidden", /* unhide later in updateDataStreamButtons, depends whether or not there are datastream buttons */
            `siteTreeButton-livedata-${deviceModel.Id}`
        );

        domElement.append(liveDataTab);

        // initial values
        setTimeout(() => { this.updateDataStreamButtons(deviceModel); }, 10); // Why the timeout: the dom element is not attached to the tree yet; 
    }

    private updateDataStreamButtons(deviceModel: DeviceViewModel) {
        var dsLen = deviceModel.DeviceDataPoints.length;
        $(`#siteTreeButton-livedata-${deviceModel.Id}`).toggleClass("hidden", dsLen <= 0);
        if (dsLen <= 0) {
            return;
        }

        var liveDataTab = $(`#sitetree-element-livedata-${deviceModel.Id}`);
        var dataStreamButtons = liveDataTab.children("div.dataStreamList");

        let availableDataPointIds = deviceModel.DeviceDataPoints.map(dp => dp.DataPointId);

        // Remove buttons not existing anymore
        dataStreamButtons.find(`div[data-datapointid]`)
            .filter((idx, elem) => availableDataPointIds.indexOf($(elem).data('datapointid')) < 0) // true if availableDataPoints doesn't contains the value
            .remove();

        // Button group Exists?
        for (var i = 0; i < dsLen; i++) {
            var deviceDataPoint = deviceModel.DeviceDataPoints[i];
            var dataStreamButton = dataStreamButtons.find(`div[data-dataPointId='${deviceDataPoint.DataPointId}']`);
            if (dataStreamButton.length === 0) {
                dataStreamButton = $("<div>",
                    {
                        /* the second div is vital! */
                        html: `<span>${deviceDataPoint.Title}</span><div><div class='siteTreeButtons'></div></div>`,
                        'class': "btn btn-default list-group-item",
                        'style': "text-align: left;",
                        'data-title': deviceDataPoint.Title,
                        'data-dataPointId': deviceDataPoint.DataPointId,
                        'data-deviceId': deviceDataPoint.DeviceConfigId,
                        'data-compressed': deviceDataPoint.IsCompressed,
                        'data-status': deviceDataPoint.State
                    })
                    .click(this.createLiveDataClickFunction(deviceDataPoint));

                dataStreamButtons.append(dataStreamButton);

                // If the stream is a ProfilePolar3D, and the Device movable, we attach the Calibration Assistant
                if (deviceDataPoint.DataFormat === "Ectn.Utilities.ProfilePolar3D" && deviceModel.IsMovable && !deviceModel.IsSelfCalibrating) {
                    TemsWebEvents.publishEvent(TemsWebEvents.CalibratableDevice, [deviceDataPoint]);
                }
            }

            // Update / Content of the button
            dataStreamButton.attr("data-status", deviceDataPoint.State);
            const buttonText = dataStreamButton.children("span");
            if (dataStreamButton.hasClass("active")) {
                buttonText.html(`${deviceDataPoint.Title} (${this
                    .translateLiveDataStatus(deviceDataPoint.State)})`);
                if (deviceDataPoint.State === DataPointState.Error) {
                    dataStreamButton.children(".dataPointError").remove();
                    dataStreamButton.append($("<div>",
                        { "class": "dataPointError", html: deviceDataPoint.StateMessage }));
                } else {
                    dataStreamButton.children(".dataPointError").remove();
                }
            } else {
                dataStreamButton.children(".dataPointError").remove();
                buttonText.html(`${deviceDataPoint.Title}`);
            }
        }
    }

    private attachPositionButton(domElement: JQuery, deviceModel: DeviceViewModel) {
        let positionTab = this.createSiteTreeDetailsPanel(`sitetree-element-position-${deviceModel.Id}`, "glyphicon-map-marker");
        domElement.append(positionTab);

        this.createSiteTreeButton(
            domElement,
            (event) => {
                $(event.currentTarget).toggleClass("active");
                positionTab.toggleClass("hidden");
            },
            "glyphicon-map-marker",
            Localization.resourceStrings.SiteTreeBuilder_ButtonTitlePosition
        );

        const positionControl = new PositionControl(deviceModel, this.siteManagerView);
        if (this.positionControls[deviceModel.Id]) {
            this.positionControls[deviceModel.Id].destroy();
        }

        this.positionControls[deviceModel.Id] = positionControl;

        positionTab.append(positionControl.Content);
    }

    private attachTriggerButton(domElement: JQuery, deviceModel: DeviceViewModel) {
        let triggerTab = this.createSiteTreeDetailsPanel(`sitetree-element-trigger-${deviceModel.Id}`, "glyphicon-camera");
        domElement.append(triggerTab);

        let content = new TriggerControl(deviceModel, this.siteManagerView.IsInEditMode);
        this.triggerControls[deviceModel.Id] = content;

        triggerTab.append(content.Content);

        this.createSiteTreeButton(
            domElement,
            (event) => {
                $(event.currentTarget).toggleClass("active");
                triggerTab.toggleClass("hidden");
            },
            "glyphicon-camera",
            Localization.resourceStrings.SiteTreeBuilder_ButtonTitleTriggerLines,
            false
        );

        // initial values
        setTimeout(() => { content.updateTriggerButton(deviceModel, this.siteManagerView.IsInEditMode); }, 0);
    }

    private attachSensorDiscoveryButton(domElement: JQuery, deviceModel: DeviceViewModel) {
        this.createSiteTreeButton(
            domElement,
            () => {
                this.siteManagerView.sensorOperationsView.changeDisplayTitle(Localization.resourceStrings.SiteView_ModalTitleSensorConfigs.replace("{0}", deviceModel.Title));
                this.siteManagerView.sensorOperationsView.showSensorConfigDiscovery(deviceModel.Id, deviceModel.SupportedSensorTypes);
            },
            "glyphicon-search",
            Localization.resourceStrings.SiteView_ButtonSensorDiscoveryTooltip
        );
    }

    private attachDetailsButton(domElement: JQuery, elementId: string) {
        this.createSiteTreeButton(
            domElement,
            (event) => {
                $(event.currentTarget).toggleClass("active");
                this.siteTreeView.showElementDetails(elementId);
            },
            "glyphicon-cog",
            Localization.resourceStrings.SiteView_ButtonDetailsTooltip,
            false,
            "showElementDetails onlyExpertMode"
        );
    }

    private translateLiveDataStatus(status: DataPointState): string {
        switch (status) {
            case DataPointState.AlwaysOn:
                return Localization.resourceStrings.SiteView_LiveDataStatusAlwaysOn;
            case DataPointState.GoingOffline:
                return Localization.resourceStrings.SiteView_LiveDataStatusGoingOffline;
            case DataPointState.Offline:
                return Localization.resourceStrings.SiteView_LiveDataStatusOffline;
            case DataPointState.GoingOnline:
                return Localization.resourceStrings.SiteView_LiveDataStatusGoingOnline;
            case DataPointState.Online:
                return Localization.resourceStrings.SiteView_LiveDataStatusOnline;
            case DataPointState.Error:
                return Localization.resourceStrings.SiteView_LiveDataStatusError;
            default:
                return `Unknown DataPointStatus: ${status}`;
        }
    }

    private createLiveDataClickFunction(dataStream: DeviceDataPointViewModel): (ev) => any {
        // why? to properly capture the values. else it is problematic inside a loop that the iterating variable is referenced and thus changed on the outside
        return ev => { TemsWebEvents.publishEvent(TemsWebEvents.ToggleDataStream, [dataStream]); };
    }

    private createLinksTab(domElement: JQuery, deviceModel: DeviceViewModel) {
        var linksTab = this.createSiteTreeDetailsPanel(`sitetree-element-links-${deviceModel.Id}`, "glyphicon-link", "display: flex; flex-direction: column;");
        domElement.append(linksTab);

        const deviceLinksControl = new DeviceLinksControl(this.siteTreeView, deviceModel);
        this.linksControls[deviceModel.Id] = deviceLinksControl;
        linksTab.append(deviceLinksControl.Content);

        this.createSiteTreeButton(
            domElement,
            (event) => {
                $(event.currentTarget).toggleClass("active");
                $(`#sitetree-element-links-${deviceModel.Id}`).toggleClass("hidden");
            },
            "glyphicon-link",
            Localization.resourceStrings.SiteTreeBuilder_ButtonTitleLinks,
            true,
            "onlyExpertMode",
            `siteTreeButton-toggleLinksTab-${deviceModel.Id}`
        );

        if (deviceModel.Links.length === 0) {
            domElement.find(`#siteTreeButton-toggleLinksTab-${deviceModel.Id}`).hide();
        }
    }

    private attachDeleteButton(domElement: JQuery, model: ElementViewModel, additionalClasses?: string) {
        this.createSiteTreeButton(
            domElement,
            () => this.siteTreeView.deleteElement(model.Id),
            "glyphicon-trash",
            Localization.resourceStrings.SiteTreeBuilder_ButtonTitleDelete,
            true,
            additionalClasses
        );
    }

    private attachEnableAllChildDevicesButton(domElement: JQuery, model: DeviceViewModel) {
        this.createSiteTreeButton(domElement,
            (event) => {
                // Check whether the "showAllChildDevices" modus is active/passive
                let isActive = !domElement.hasClass("showAllChildDevices");
                domElement.toggleClass("showAllChildDevices", isActive);

                $(event.currentTarget).toggleClass("active", isActive);

                if (isActive) {
                    // Show all child devices and show the enable / disable buttons
                    domElement.find("ul").children("li")
                        .toggleClass("hidden", false)
                        .find("button.toggleEnableControl")
                        .toggleClass("hidden", false);
                } else {
                    // Hide the enable / disable buttons
                    domElement.find("ul").children("li")
                        .find("button.toggleEnableControl")
                        .toggleClass("hidden", true);

                    // Trigger a refresh that will hide the disabled child devices
                    this.siteManagerView.forceSiteModelUpdate();
                }
            },
            "glyphicon-check",
            Localization.resourceStrings.SiteTreeBuilder_ButtonTitleEnableAllChildren,
            true,
            "toggleShowAllControl"
        );
    }

    private attachEnableDeviceButton(domElement: JQuery, model: DeviceViewModel) {
        this.createSiteTreeButton(domElement, (event) => this.siteTreeView.deviceToggleEnableState(model.Id, $(event.currentTarget).children("span")),
            model.IsEnabled ? "glyphicon-check" : "glyphicon-unchecked",
            Localization.resourceStrings.SiteTreeBuilder_ButtonTitleEnableDevice,
            true,
            "toggleEnableControl hidden");
    }

    private attachRoadSignButton(domElement: JQuery, model: LocationViewModel) {
        const signTab = this.createSiteTreeDetailsPanel(`sitetree-element-roadsign-${model.Id}`, "glyphicon-tag");
        signTab.append(`<div id="sitetree-element-notsignednotice-${model.Id}">${Localization.resourceStrings.SiteView_RoadSignatureInvalid}</div>`);
        signTab.append(
            $(`<form action="/api/SiteEdit/CreateRoadSignature" class="form-vertical" id="signRoadForm-${model.Id}" method="post" role="form" >
                <div class="form-group">
                    <label>${Localization.resourceStrings.SiteView_RoadSignatureNameLabel}</label>
                    <input type="text" class="form-control signatureName" name="name">
                </div>
                <div class="form-group">
                    <label>${Localization.resourceStrings.SiteView_RoadSignatureNotesLabel}</label>
                    <textarea class="form-control signatureNotes" rows="5" name="notes"></textarea>
                </div>
                <div class="form-group" id="signRoadForm-${model.Id}-signatureDate">
                    <label>${Localization.resourceStrings.SiteView_RoadSignatureDateLabel}</label>
                    <input type="text" class="form-control signatureDate" disabled>
                </div>
                <input type="hidden" name="id" value="${model.Id}">
                <div class="editControl text-right">
                    <button id="sitetree-element-roadsign-addbutton-${model.Id}" class="btn btn-primary" type="submit">
                        <span class="button-icon glyphicon glyphicon-plus clickable"></span>${Localization.resourceStrings.SiteView_RoadSignatureAddButton}
                    </button>
                </div>
            </form>`).on('submit', (event) => {
                var form = $(event.target);

                this.siteManagerView.changeUserLevel(ViewModelUserLevel.Service).catch().then(() => {
                    $(`#sitetree-element-roadsign-addbutton-${model.Id}`).prop("disabled", true);
                    $.ajax({
                        url: form.attr('action'),
                        type: form.attr('method'),
                        data: form.serialize(),
                        complete: (data) => {
                            $(`#sitetree-element-roadsign-addbutton-${model.Id}`).prop("disabled", false);
                            this.siteManagerView.forceSiteModelUpdate();
                        },
                        error: (xhr, err) => {
                            this.siteManagerView.showAlertMessage(
                                Localization.resourceStrings.SiteView_RoadSignatureFailed,
                                xhr.responseJSON.Message,
                                Localization.resourceStrings.SiteView_ErrorExceptionMessageTemplate
                            );
                        }
                    });
                });
                
                return false;
            })
        );

        signTab.append(
            $(`<form class="editControl text-right">
                <button id="sitetree-element-roadsign-removebutton-${model.Id}" class="btn btn-primary" type="submit">
                    <span class="button-icon glyphicon glyphicon-trash clickable"></span>${Localization.resourceStrings.SiteView_RoadSignatureRemoveButton}
                </button>
            </form>`).submit((ev) => {
                this.siteManagerView.changeUserLevel(ViewModelUserLevel.Service).then(() => {
                    $.ajax({
                        url: `/api/SiteEdit/RemoveRoadSignature/${model.Id}`,
                        method: "POST"
                    }).done(() => {
                        this.siteManagerView.forceSiteModelUpdate();
                    }).fail((xhr, err) => {
                        this.siteManagerView.showAlertMessage(
                            Localization.resourceStrings.LayoutView_ErrorTitleGeneral,
                            xhr.responseJSON.Message,
                        );
                    });
                });

                return false;
            })
        );

        domElement.append(signTab);

        this.createSiteTreeButton(
            domElement, (event) => {
                $(event.currentTarget).toggleClass("active");
                signTab.toggleClass("hidden");
            },
            "glyphicon-tag",
            Localization.resourceStrings.SiteTreeBuilder_ButtonTitleRoadSign,
            false,
            "",
            `sitetree-element-signaturebutton-${model.Id}`
        );
    }

    private attachRoadLanesButton(domElement: JQuery, locationModel: LocationViewModel) {
        let roadTab = this.createSiteTreeDetailsPanel(`sitetree-element-roadconfig-${locationModel.Id}`, "glyphicon-road");
        let table = $("<table>", { id: `sitetree-lane-table-${locationModel.Id}`, class: "table table-hover table-condensed" })
            .append($("<thead>", {
                html:
                `<tr>
                        <th>${Localization.resourceStrings.SiteView_RoadConfigLane}</th>
                        <th>${Localization.resourceStrings.SiteView_RoadConfigWidth}</th>
                        <th>${Localization.resourceStrings.SiteView_RoadConfigDirection}</th>
                        <th class="isHardShoulderColumn" >${Localization.resourceStrings.SiteView_RoadConfigIsHardShoulder}</th>
                    </tr>`
            }));

        if (locationModel.MaxLanes > 1) {
            const currentNrOfLanes = locationModel.Lanes.length > 0 ? locationModel.Lanes.length : 1;

            let laneOptions = [];
            for (let i = 1; i <= locationModel.MaxLanes; i++) { // Note 1 to MaxLanes!
                laneOptions.push(`{value: ${i}, text: '${i}'}`);
            }

            let numberOfLanesEditable = $("<span>", {
                id: `sitetree-lane-number-on-road-${locationModel.Id}`,
                "data-value": currentNrOfLanes,
                "data-type": "select",
                "data-source": `[${laneOptions.join(",")}]`,
                "class": "siteEditable editControl control-inline"
            });

            numberOfLanesEditable.editable("setValue", [currentNrOfLanes, false]);
            numberOfLanesEditable.on("save", (e, params) => this.siteTreeView.configureNumberOfLanes(locationModel.Id, params.newValue));
            roadTab.append($("<div>", { html: Localization.resourceStrings.SiteTreeBuilder_LabelRoadLaneNumber }).append(numberOfLanesEditable));
        }

        let tbody = $("<tbody>");
        table.append(tbody);
        locationModel.Lanes.forEach(lane => this.createLane(tbody, lane, locationModel.Id, locationModel.Lanes.length));

        roadTab.append(table);
        domElement.append(roadTab);

        this.createSiteTreeButton(
            domElement, (event) => {
                $(event.currentTarget).toggleClass("active");
                roadTab.toggleClass("hidden");
            },
            "glyphicon-road",
            Localization.resourceStrings.SiteTreeBuilder_ButtonTitleRoad,
            false
        );
    }
}
